import { useEffect, useMemo, useState } from "react";
import departmentForecastApi from "@/features/Sale/HeaderDashboard/api/departmentForecast.api";
import { DEPARTMENT_FORECAST_UPDATED_EVENT } from "@/features/Sale/HeaderDashboard/lib/departmentForecastEvents";
import { DepartmentForecastSummary } from "@/features/Sale/HeaderDashboard/model/departmentForecast.types";
import { useDashboard } from "../../../lib/useDashboard";
import { DEPARTMENTS_PLAN_IDS } from "../../../model/dashboard.types";
import { getFirstDayOfCurrentMonth, getLastDayOfCurrentMonth, parseLocalDateString, toLocalDateString } from "@/shared/lib/dates";
import { useAuth } from "@/features/Auth/lib/useAuth";
import { canSeeCommercialOverview } from '@/features/Auth/lib/salesAccess';

type DepartmentForecastPlanResult = {
  opportunity: number | null;
  profit: number | null;
};

type DepartmentsForecastMap = Record<number, DepartmentForecastPlanResult>;

function getRangeMonths(dateStart: string, dateEnd: string) {
  const start = parseLocalDateString(dateStart);
  const end = parseLocalDateString(dateEnd);

  if (!start || !end || start > end) {
    return [];
  }

  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  const result: Array<{ year: number; month: number }> = [];

  while (cursor <= last) {
    result.push({
      year: cursor.getFullYear(),
      month: cursor.getMonth() + 1,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return result;
}

function getMonthDateRange(year: number, month: number) {
  return {
    start: new Date(year, month - 1, 1),
    end: new Date(year, month, 0),
  };
}

function getOverlappedDays(
  rangeStart: Date,
  rangeEnd: Date,
  monthStart: Date,
  monthEnd: Date
) {
  const start = rangeStart > monthStart ? rangeStart : monthStart;
  const end = rangeEnd < monthEnd ? rangeEnd : monthEnd;

  if (start > end) {
    return 0;
  }

  return Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
}

function sumForecastByMonths(
  summaries: DepartmentForecastSummary[],
  rangeMonths: Array<{ year: number; month: number }>,
  dateStart: string,
  dateEnd: string
): DepartmentForecastPlanResult {
  const rangeStart = parseLocalDateString(dateStart);
  const rangeEnd = parseLocalDateString(dateEnd);

  if (!rangeStart || !rangeEnd) {
    return { opportunity: null, profit: null };
  }

  const summaryByYear = new Map(summaries.map((summary) => [summary.year, summary]));
  let opportunity = 0;
  let profit = 0;
  let hasAnyValue = false;

  for (const rangeMonth of rangeMonths) {
    const summary = summaryByYear.get(rangeMonth.year);
    const monthData = summary?.months.find((item) => item.month === rangeMonth.month);

    if (!monthData) {
      continue;
    }

    const { start: monthStart, end: monthEnd } = getMonthDateRange(rangeMonth.year, rangeMonth.month);
    const overlappedDays = getOverlappedDays(rangeStart, rangeEnd, monthStart, monthEnd);
    const daysInMonth = monthEnd.getDate();

    if (overlappedDays <= 0 || daysInMonth <= 0) {
      continue;
    }

    const ratio = overlappedDays / daysInMonth;
    opportunity += monthData.forecast.turnover * ratio;
    profit += monthData.forecast.grossProfit * ratio;
    hasAnyValue = true;
  }

  if (!hasAnyValue) {
    return { opportunity: null, profit: null };
  }

  return {
    opportunity: Math.round(opportunity),
    profit: Math.round(profit),
  };
}

export function useDepartmentsForecastPlans() {
  const { dateStart, dateEnd } = useDashboard();
  const { user } = useAuth();
  const director = canSeeCommercialOverview(user);
  const start = dateStart ?? (director ? toLocalDateString(getFirstDayOfCurrentMonth()) : null);
  const end = dateEnd ?? (director ? toLocalDateString(getLastDayOfCurrentMonth()) : null);

  const [refreshKey, setRefreshKey] = useState(0);
  const [forecasts, setForecasts] = useState<DepartmentsForecastMap>({});

  useEffect(() => {
    const handleForecastUpdated = () => {
      setRefreshKey((prev) => prev + 1);
    };

    window.addEventListener(DEPARTMENT_FORECAST_UPDATED_EVENT, handleForecastUpdated);

    return () => {
      window.removeEventListener(DEPARTMENT_FORECAST_UPDATED_EVENT, handleForecastUpdated);
    };
  }, []);

  const rangeMonths = useMemo(() => {
    if (!start || !end) {
      return [];
    }

    return getRangeMonths(start, end);
  }, [end, start]);

  useEffect(() => {
    if (!start || !end || rangeMonths.length === 0) {
      setForecasts({});
      return;
    }

    let cancelled = false;

    const loadForecasts = async () => {
      try {
        const years = Array.from(new Set(rangeMonths.map((item) => item.year)));

        const entries = await Promise.all(
          DEPARTMENTS_PLAN_IDS.map(async (departmentId) => {
            try {
              const responses = await Promise.all(
                years.map(async (year) => {
                  const response = await departmentForecastApi.getDepartmentForecast(departmentId, year);
                  return response.success ? response.data : null;
                })
              );

              const summaries = responses.filter(Boolean) as DepartmentForecastSummary[];

              return [
                departmentId,
                sumForecastByMonths(summaries, rangeMonths, start, end),
              ] as const;
            } catch {
              return [
                departmentId,
                { opportunity: null, profit: null },
              ] as const;
            }
          })
        );

        if (cancelled) {
          return;
        }

        setForecasts(Object.fromEntries(entries));
      } catch {
        if (!cancelled) {
          setForecasts({});
        }
      }
    };

    loadForecasts();

    return () => {
      cancelled = true;
    };
  }, [end, rangeMonths, start, refreshKey]);

  return forecasts;
}
